import React, { useEffect, useRef, useState } from 'react';
import { MoonIcon, NotificationsOutlined, Search, SunIcon } from '@/ui/icons';
import { useExpFeatures } from '@/hooks';
import { resolveThemeMode, toggleThemeMode } from '@/lib';

import './Topbar.css';

// Topbar — app chrome: breadcrumb + (per-page pills) + search (⌘K) +
// theme toggle + notification bell. The account/org menu lives in the SIDEBAR
// footer (the placement), not here. `onOpenSearch` opens the command
// palette (wired in MainLayout); the bell shows a panel of in-app avisos.
export default function Topbar({
  breadcrumb = [],
  pills = [],
  hasNotification = false,
  onOpenSearch,
  notifications = [],
}) {
  const [themeMode, setThemeMode] = useState(resolveThemeMode);
  const [bellOpen, setBellOpen] = useState(false);
  const bellRef = useRef(null);
  // The in-app avisos surfaced by the bell are experimental; hide it when the
  // experimental-features flag is off (Sidebar gates the same way).
  const [exp] = useExpFeatures();

  useEffect(() => {
    if (!bellOpen) return undefined;
    function onDocClick(e) {
      if (bellRef.current && !bellRef.current.contains(e.target)) {
        setBellOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [bellOpen]);

  return (
    <header className="lb-topbar">
      <nav className="lb-topbar__breadcrumb" aria-label="Breadcrumb">
        {breadcrumb.map((segment, i) => {
          const isLast = i === breadcrumb.length - 1;
          const label = typeof segment === 'string' ? segment : segment?.label;
          const onClick =
            segment && typeof segment === 'object'
              ? segment.onClick
              : undefined;
          return (
            <React.Fragment key={i}>
              {i > 0 && <span aria-hidden="true"> / </span>}
              {isLast ? (
                <b>{label}</b>
              ) : onClick ? (
                <button
                  type="button"
                  className="lb-topbar__crumb-link"
                  onClick={onClick}
                >
                  {label}
                </button>
              ) : (
                <span>{label}</span>
              )}
            </React.Fragment>
          );
        })}
      </nav>

      <div className="lb-topbar__spacer" />

      {pills.length > 0 && (
        <div className="lb-topbar__pills" role="toolbar" aria-label="Acciones">
          {pills.map((pill, i) => (
            <button
              key={i}
              className="lb-topbar__pill"
              type="button"
              onClick={pill.onClick}
              disabled={pill.disabled}
              title={pill.title}
            >
              {pill.label}
              {pill.kbd && <kbd>{pill.kbd}</kbd>}
            </button>
          ))}
        </div>
      )}

      {onOpenSearch && (
        <button
          type="button"
          className="lb-topbar__search"
          onClick={onOpenSearch}
          title="Buscar (⌘K)"
          aria-label="Buscar (⌘K)"
        >
          <Search size={15} />
          <span>Buscar</span>
          <kbd>⌘K</kbd>
        </button>
      )}

      <button
        className="lb-topbar__icon"
        type="button"
        aria-label={
          themeMode === 'dark'
            ? 'Cambiar a modo claro'
            : 'Cambiar a modo oscuro'
        }
        title={themeMode === 'dark' ? 'Modo claro' : 'Modo oscuro'}
        onClick={() => setThemeMode(toggleThemeMode())}
      >
        {themeMode === 'dark' ? <SunIcon size={17} /> : <MoonIcon size={17} />}
      </button>

      {exp && (
        <div className="lb-topbar__bell-wrap" ref={bellRef}>
          <button
            className="lb-topbar__icon"
            type="button"
            aria-label="Avisos"
            aria-haspopup="menu"
            aria-expanded={bellOpen}
            onClick={() => setBellOpen((v) => !v)}
          >
            <NotificationsOutlined size={17} />
            {hasNotification && (
              <span className="lb-topbar__icon-dot" aria-hidden="true" />
            )}
          </button>
          {bellOpen && (
            <div className="lb-topbar__bell-pop" role="menu">
              <div className="lb-topbar__bell-head">
                <span className="lb-topbar__bell-kicker">Avisos</span>
              </div>
              {notifications.length === 0 ? (
                <div className="lb-topbar__bell-empty">
                  Sin avisos por ahora.
                </div>
              ) : (
                notifications.map((n, i) => (
                  <button
                    key={i}
                    type="button"
                    className="lb-topbar__bell-item"
                    onClick={() => {
                      setBellOpen(false);
                      n.onClick?.();
                    }}
                  >
                    <div className="lb-topbar__bell-txt">{n.txt}</div>
                    {n.sub && (
                      <div className="lb-topbar__bell-sub">{n.sub}</div>
                    )}
                  </button>
                ))
              )}
              <div className="lb-topbar__bell-foot">
                Avisos de tus diligencias y pagos, distintos de las
                notificaciones judiciales de cada causa.
              </div>
            </div>
          )}
        </div>
      )}
    </header>
  );
}
